function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useRef, useEffect } from "react";
import styled, { css } from "styled-components";
import { Editor, EditorState, RichUtils, CompositeDecorator } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { FlexColumn, Icon, useIsMobile, Footnote } from "../";
import "draft-js/dist/Draft.css";
import { ToggleLink, ToggleOption, EmojiPicker, TextEditorProvider, Toolbar, getSelectedText, linkStrategy, Link, useTextEditorContext, handleLinkEntityClick } from "./components";
const styleMap = {
  BOLD: {
    fontWeight: 700
  }
};
const inlineStylesArray = [{
  style: "BOLD",
  icon: /*#__PURE__*/React.createElement(Icon, {
    selector: true,
    name: "Bold"
  })
}, {
  style: "UNDERLINE",
  icon: /*#__PURE__*/React.createElement(Icon, {
    selector: true,
    name: "Underline"
  })
}, {
  style: "ITALIC",
  icon: /*#__PURE__*/React.createElement(Icon, {
    selector: true,
    name: "Italic"
  })
}];
export const TextEditor = props => {
  return /*#__PURE__*/React.createElement(TextEditorProvider, null, /*#__PURE__*/React.createElement(TextEditorInternal, props));
};
export const TextEditorInternal = ({
  value,
  onChange,
  html,
  getHtml,
  disabled = false,
  error = false,
  isSMS = false,
  hideInlineStyles = isSMS,
  showCharacterCount = isSMS,
  onCharacterCount,
  toolbarChildren,
  hideFocusState,
  className = "",
  emojiList,
  ...props
}) => {
  const {
    linkModal,
    setLinkModal,
    editorFocus,
    setEditorFocus,
    setTextSelected,
    setCurrentLink
  } = useTextEditorContext();
  const isMobile = useIsMobile();
  const contentState = stateFromHTML(value || html || "");
  const decorator = new CompositeDecorator([{
    strategy: linkStrategy,
    component: props => {
      const {
        contentState,
        blockKey,
        entityKey,
        start
      } = props;
      return /*#__PURE__*/React.createElement(Link, _extends({
        onClick: (e, url) => {
          e.preventDefault();
          const contentBlock = contentState.getBlockForKey(blockKey);
          const entity = contentBlock.getEntityAt(start);
          const position = handleLinkEntityClick();
          setCurrentLink(url);
          setLinkModal({
            open: true,
            position,
            entity,
            entityKey
          });
        }
      }, props));
    }
  }]);
  const [editorState, setEditorState] = useState(() => EditorState.createWithContent(contentState, decorator));
  const contentLength = editorState?.getCurrentContent()?.getPlainText("")?.length;
  const initialCount = !!contentLength ? contentLength : 0;
  const [count, setCount] = useState(initialCount);
  useEffect(() => {
    onCharacterCount && onCharacterCount(count);
  }, []);

  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      setEditorState(newState);
      return "handled";
    }

    return "not-handled";
  };

  const handleEditorChange = state => {
    const selectedText = getSelectedText(state);

    if (selectedText.length > 0) {
      setTextSelected(true);
    } else {
      setTextSelected(false);
    }

    setEditorState(state);
    const contentState = state.getCurrentContent();
    const count = contentState.getPlainText("").length;
    setCount(count);
    onCharacterCount && onCharacterCount(count);

    if (contentState?.hasText()) {
      const htmlState = stateToHTML(contentState);
      onChange && onChange(htmlState);
      getHtml && getHtml(htmlState);
    } else {
      onChange && onChange("");
      getHtml && getHtml("");
    }
  };

  const editorRef = useRef();

  const handleFocus = () => {
    if (!disabled) {
      editorRef.current.focus();
      setEditorFocus(true);
    }
  };

  const editorContainerRef = useRef();

  const inlineIsActive = style => {
    return !editorFocus ? false : editorState.getCurrentInlineStyle().has(style);
  };

  const toggleInlineStyle = style => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const inlineStyles = inlineStylesArray.map(({
    icon,
    style
  }) => {
    const active = inlineIsActive(style);
    return /*#__PURE__*/React.createElement(ToggleOption, {
      icon: icon,
      key: style,
      onClick: !editorFocus ? () => handleFocus() : () => toggleInlineStyle(style),
      active: active,
      editorFocus: editorFocus,
      isMobile: isMobile
    });
  });
  const countMessage = isSMS && count > 160 ? `${Math.ceil(count / 160)} messages` : `${count} character${count !== 1 ? "s" : ""}`;
  return /*#__PURE__*/React.createElement(TextEditorContainer, {
    ref: editorContainerRef,
    hasFocus: editorFocus && !hideFocusState,
    hasError: error,
    disabled: disabled,
    className: className
  }, /*#__PURE__*/React.createElement(TextEditorContent, {
    disabled: disabled,
    onClick: handleFocus
  }, /*#__PURE__*/React.createElement(Editor, _extends({
    onChange: handleEditorChange,
    editorState: editorState,
    customStyleMap: styleMap,
    onFocus: () => setEditorFocus(true),
    onBlur: () => setEditorFocus(false),
    handleKeyCommand: handleKeyCommand,
    stripPastedStyles: true,
    readOnly: disabled,
    ref: editorRef
  }, props))), /*#__PURE__*/React.createElement(Toolbar, {
    editorContainerRef: editorContainerRef?.current,
    handleFocus: handleFocus,
    hideInlineStyles: hideInlineStyles,
    onClick: () => {
      if (!editorFocus && !linkModal.open && !disabled) {
        handleFocus();
      }
    },
    yCenter: true
  }, !isMobile && toolbarChildren, !isMobile && /*#__PURE__*/React.createElement(EmojiPicker, {
    editorState: editorState,
    onChange: handleEditorChange,
    handleFocus: handleFocus,
    emojiList: emojiList
  }), !hideInlineStyles && inlineStyles, /*#__PURE__*/React.createElement(ToggleLink, {
    editorState: editorState,
    onChange: handleEditorChange,
    handleFocus: handleFocus,
    disabled: disabled
  }), showCharacterCount && /*#__PURE__*/React.createElement(TextEditorCharacterCount, null, countMessage)));
};
export const TextEditorContent = styled.div`
  box-sizing: border-box;
  padding: 12px;
  padding-bottom: 56px;
  position: relative;
  z-index: 0;
  min-height: 160px;
  width: 100%;

  // Global Block
  .public-DraftStyleDefault-block {
    margin: 0;
    ${props => props.theme.typography.h4};
    font-family: "Overpass", "Apple Color Emoji", "Segoe UI Emoji",
      "Segoe UI Symbol", sans-serif;
    color: ${props => props.disabled ? props.theme.palette.gray.primary : props.theme.palette.black};

    // Link Block
    a.link {
      color: ${props => props.disabled ? props.theme.palette.gray.primary : props.theme.palette.purple.primary};
      cursor: pointer;
      text-decoration: underline;
    }
  }
  // Placeholder Block
  .public-DraftEditorPlaceholder-root {
    ${props => props.theme.typography.placeholder};
    line-height: 15px;
    color: ${props => props.theme.palette.gray.primary};
    margin-top: 2px;
  }

  *::selection {
    background: ${props => !props.disabled && props.theme.palette.yellow.lighter};
  }
  @media only screen and ${props => props.theme.media.small} {
    min-height: 112px;
    padding-bottom: 12px;
  }
`;
export const TextEditorContainer = styled(FlexColumn)`
  border: 1px solid ${props => props.theme.palette.gray.primary};
  border-radius: 4px;
  position: relative;
  align-items: flex-start;
  ${props => props.hasFocus && !props.disabled && css`
      ${props => props.theme.input.focus};
    `};
  ${props => props.hasError && css`
      ${props => props.theme.input.error};
      border-color: ${props.theme.input.error.borderColor};
      border-width: 2px;
      margin: -1px;
      &:focus {
        border-color: ${props.theme.input.error.borderColor};
        box-shadow: 0 0 4px 0 ${props.theme.input.error.borderColor};
      }
    `};
  ${props => props.disabled && css`
      ${props => props.theme.input.disabled};
      ${props.hasError && `background-color: ${props.theme.palette.red.lightest};`};
      cursor: default;
    `};
`;
export const TextEditorCharacterCount = styled(Footnote)`
  color: ${props => props.theme.palette.gray.primary};
  margin-left: 12px;
  @media only screen and ${props => props.theme.media.small} {
    display: none;
  }
`;